/* hp-motion.js — motion for the new homepage (/?view=staging) and the PDP preview (?view=starter-set).
   Port of the v4 prototype engine (homepage-pdp-v4, lines 2317–2600) to the real window viewport. Staging build, Oct 2026.

   Rules (v3 §9.1):
   - transform + opacity only; prices, conditions, legend, basis lines, label texts and nutrition figures never move;
   - scroll-linked: the Set assembly and the Pasta fan (founder 9 Oct 2026: back to the launch page's fan); everything
     else is an IntersectionObserver one-shot;
   - start states exist ONLY while this script has armed an element (attribute data-hp-wait, CSS in section-hp.css).
     No JS, a failed module, prefers-reduced-motion and the theme editor all show the finished page (the server
     renders every end state, incl. the landed Set and the final numbers);
   - an element whose bottom is already above the screen at boot keeps its end state; from 1024 px a Set whose moving
     products show on the screen at load slides in once, time-based, from the start pose that snippets/set-intro set
     before first paint (founder report 3 Nov 2026); without that mark the Set stays finished there, at a boot further
     down where one already shows on the current screen before landing, and after a width change into a first-screen
     size (assemble, SEEN, intro, finish);
   - IO + rAF only (works in the Instagram/Facebook webviews), no scroll-driven CSS, no animation-timeline.

   Hooks (markup contract for the sections):
   - house reveal   [data-hp-reveal] (one block) · [data-hp-reveal-group] > [data-hp-reveal-item] (style="--i:n",
                    150 ms steps, max 4). Per-section timing via CSS vars --hp-rv-o / --hp-rv-t / --hp-rv-step.
   - "set"          root [data-hp-motion="set"] containing [data-hp-cmp] (snippets/set-composition): .hp-ly layers with
                    data-x/y/s, data-i (stagger of the phone motion) and data-dir (its side; on desktop only the fallback
                    for a layer without a part class), .hp-lb labels. .hp-cmp--solo never assembles.
   - "pasta"        root [data-hp-motion="pasta"] (optional data-hp-count-ms): [data-hp-pasta-main] (phone image),
                    [data-hp-pasta-fan] with [data-fan="main"] + [data-fan="card"] (desktop), [data-hp-pasta-proof] with
                    [data-ps="before"|"arrow"|"after"], [data-hp-count data-from data-to data-dec],
                    [data-bar="before"], [data-bar="after" data-r="0.552"] (r = before / after).
   - "scene"        root [data-hp-motion="scene"] (one Ein-Löffel scene): [data-hp-lo="main"], [data-hp-lo="sec"]
                    (style="--j:n"), [data-hp-lo="text"].
   - "bedarf"       root [data-hp-motion="bedarf"] (optional data-hp-widen, data-hp-count-ms): [data-hp-stat] containing
                    [data-hp-count]. The grey layer is the root's ::before; it widens on desktop via .is-go.
   - "dishes"       root [data-hp-motion="dishes"]: [data-cg-item] each with [data-hp-count] and [data-addbar data-r].
   A counted figure is aria-hidden; a visually-hidden final value sits next to it; the HTML holds the final value. */
(function () {
  'use strict';
  if (window.__hpMotion) return;
  window.__hpMotion = true;

  var reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  if (reduce || (window.Shopify && window.Shopify.designMode)) return;
  if (!('IntersectionObserver' in window) || !window.requestAnimationFrame) return;

  var EASE_IO = 'cubic-bezier(.25,.1,.25,1)';
  var EASE_OUT = 'cubic-bezier(.22,1,.36,1)';
  var WAIT = 'data-hp-wait';

  /* ---------- helpers ---------- */
  function each(list, fn) { Array.prototype.forEach.call(list, fn); }
  function clamp(v) { return v < 0 ? 0 : v > 1 ? 1 : v; }
  function viewH() { return (window.visualViewport && window.visualViewport.height) || window.innerHeight; }
  function shown(el) { return !!(el && el.getClientRects().length); }
  function passed(el) { return el.getBoundingClientRect().bottom <= 1; }
  function arm(el) { if (el) el.setAttribute(WAIT, ''); }
  function disarm(el) { if (el) el.removeAttribute(WAIT); }
  function num(v, d) { var n = parseFloat(v); return isNaN(n) ? d : n; }
  function fmt(v, dec) { return v.toFixed(dec).replace('.', ','); }

  /* fire fn once when at least thr of el is inside the screen */
  function once(el, thr, fn) {
    var io = new IntersectionObserver(function (es) {
      es.forEach(function (e) {
        if (e.isIntersecting && e.intersectionRatio >= thr - 0.001) { io.disconnect(); fn(); }
      });
    }, { threshold: [0, thr] });
    io.observe(el);
  }
  function anim(el, frames, o) { return el && el.animate ? el.animate(frames, o) : null; }
  /* one element in: transform fromT → none (tDur, ease) and opacity 0 → 1 (oDur, oEase), both after delay ms.
     fill: backwards, so the start state also covers the delay; the end state is the plain CSS state. */
  function rise(el, fromT, delay, tDur, oDur, ease, oEase) {
    if (!el) return;
    anim(el, [{ transform: fromT }, { transform: 'none' }], { duration: tDur, delay: delay, easing: ease, fill: 'backwards' });
    anim(el, [{ opacity: 0 }, { opacity: 1 }], { duration: oDur, delay: delay, easing: oEase || ease, fill: 'backwards' });
  }
  /* countUp: rAF, ease-out cubic 1 − (1 − t)³, German comma, writes exactly `to` at the end (t is clamped);
     every run has its own rAF id. onFrame(e) is called every frame (bars in sync), e = 1 on the last frame. */
  function countUp(el, from, to, dur, dec, onFrame) {
    var t0 = performance.now();
    function step(now) {
      var t = Math.min(1, Math.max(0, (now - t0) / dur));
      var e = 1 - Math.pow(1 - t, 3);
      el.textContent = t >= 1 ? el.__hpFinal || fmt(to, dec) : fmt(from + (to - from) * e, dec);
      if (onFrame) onFrame(t >= 1 ? 1 : e);
      if (t < 1) requestAnimationFrame(step);
    }
    requestAnimationFrame(step);
  }
  function countEl(el) {
    var dec = parseInt(el.getAttribute('data-dec'), 10);
    if (isNaN(dec)) dec = 1;
    el.__hpFinal = el.textContent;
    return { el: el, from: num(el.getAttribute('data-from'), 0), to: num(el.getAttribute('data-to'), 0), dec: dec };
  }
  function headerH() {
    var h = document.querySelector('.launch-header, header.header--sticky');
    return h ? Math.round(h.getBoundingClientRect().height) : 0;
  }

  /* a module that throws leaves its root in the end state */
  function fail(root) {
    disarm(root);
    each(root.querySelectorAll('[' + WAIT + ']'), disarm);
    each(root.querySelectorAll('.hp-ly'), function (l) {
      if (l.__hpTf0 != null) { l.style.transform = l.__hpTf0; l.style.opacity = ''; l.style.willChange = ''; }
    });
    each(root.querySelectorAll('[data-hp-count]'), function (c) { if (c.__hpFinal != null) c.textContent = c.__hpFinal; });
    each(root.querySelectorAll('[data-bar], [data-addbar]'), function (b) { b.style.transform = ''; });
  }

  /* ---------- house reveal ----------
     IO threshold 0, rootMargin 0 0 -10% 0, once. One group at a time: a trigger that fires while the previous one is
     still moving waits until ≈ 500 ms into it, capped at 600 ms. Items of a group step by 150 ms (CSS, max 4 steps). */
  var slot = 0;
  function reveals(scope) {
    var trigs = scope.querySelectorAll('[data-hp-reveal], [data-hp-reveal-group]');
    if (!trigs.length) return;
    var io = new IntersectionObserver(function (es) {
      es.forEach(function (e) {
        if (!e.isIntersecting) return;
        var t = e.target, now = performance.now();
        var d = Math.min(600, Math.max(0, slot - now));
        slot = now + d + 500;
        t.style.setProperty('--d', Math.round(d) + 'ms');
        disarm(t);
        io.unobserve(t);
      });
    }, { rootMargin: '0px 0px -10% 0px', threshold: 0 });
    each(trigs, function (t) {
      if (t.__hpRv || passed(t) || !shown(t)) return;
      t.__hpRv = true;
      arm(t);
      io.observe(t);
    });
  }

  /* ---------- the Set assembly (homepage Set moment, /pages/proteinreich-kochen, Beutel-PDP band): rAF only ----------
     Hans 27 Oct 2026 (built 28 Oct): later and in view. Until then p ran from the composition's top at the bottom edge
     (p 0) to 35 % (p 1) with an ease-out, so two thirds of the travel happened while the Set sat at the bottom edge.
     p = 0 when the composition's top reaches 70 % of the screen height, p = 1 when the composition is centred in the
     visible area between the header and a bottom buy bar:
     p = clamp((0.7·vh − top) / (0.7·vh − (a + (b − a − h) / 2))), top / h = the composition's box,
       a = the header's bottom edge, clamped to 0 … vh: the header counts only while it covers the top of the screen
           (pinned on the homepage; on the Beutel page and the landing page it scrolls away, so a = 0 there);
       b = the top edge of a visible bottom buy bar (#product-sticky-bar: the Beutel page's, or the landing page's
           .hp-sb), vh while the bar is hidden (moved below the screen), display: none or absent.
     Founder 28 Oct: „center the Set on the Beutel page too“. With the header height always subtracted and the bar
     ignored, the Set landed 58–70 px below the middle of the visible screen there; now within 5 px of it on the
     homepage, the Beutel page and the landing page (360–1440 px, measured 28 Oct). Box, a and b are read together once
     per frame, before the frame's writes (no forced layout); while the bar slides in or out (0.2 s) the frame loop
     follows it until it rests, so a bar that toggles mid-assembly moves the layers smoothly, not in one jump.
     v3 (founder 1 Nov 2026, built the same day): „the placement of the products at the beginning still looks off … they
     come from left/right, so they only move horizontally“, or „the pouch is static and already on the final place and
     only the rest moves“. Both, combined. v2 (28 Oct) started the Beutel above its spot on desktop (it hung high in the
     band), the Dose up to a quarter of its width past the left screen edge (cut off at the bottom left), the Löffel
     below, all three at scale 0.7 and growing; below 1024 px each layer came from its block's enter_from (the Beutel
     from the right, partly past the screen edge, the Dose and the Löffel from the left).
     Two motions since the founder's phone test of the v3 preview (1 Nov 2026: „now the animation is not enough … the
     animation we had before looked good or at least better on mobile but not desktop“):
     PHONES and tablets below 1024 px („fly“): the live motion of 28 Oct (v2), unchanged: every layer, the Beutel too,
              comes from its block's enter_from (Beutel from the right, Dose and Löffel from the left), D from 32 %
              (390 px) to 60 % (558 px and wider) of the stage, scale 0.7 → 1 and opacity min(1, 2.5·sp), stagger by
              block order (data-i: sp = clamp(p / (1 − 0.12·i))), start at 80 %, hidden at the start pose until the
              images have decoded. The first-screen rules below do not apply (as live).
     DESKTOP, 1024 px and wider („slide“, v3), everything below:
     The Beutel (the layer of part pouch) never moves: this script writes no inline style on it, so it stands at the
     server's landed transform, fully opaque, from the first frame. Every other layer slides in horizontally only, y and scale
     stay at the landed values: the Dose (tin) from the left, the Löffel (spoon) from the right. The block's enter_from
     (data-dir) does not count for these parts; it stays as the fallback for a layer without a part class.
     Across 1024 px (a tablet turned, a window resized) the other motion takes over from the landed styles (mode()).
     mover k (0, 1, … in block order, the Beutel not counted; today the Dose 0, the Löffel 1):
              sp = clamp(p / (1 − 0.12·k)), se = sp²·(3 − 2·sp) (smoothstep: the travel happens mid-screen),
              transform = translate(x% + dir·D·(1 − se)%, y%) scale(s). No opacity: the movers are solid at every p.
              Mover 0 (the Dose) lands last, at p 1; mover 1 (the Löffel) at p 0.88; the labels follow at p ≥ 0.995.
              No fade (1 Nov 2026, after the preview check): with the Beutel standing opaque, any fade let it show
              through the half-faded Dose (its kraft edge and „99% PROTEIN“ label through the steel, the tin reading
              as frosted glass) while the Set was already in view; v2's 2.5·sp and the first v3's 4·sp both did, for
              ≈ 80 px of scroll on a 390 px phone at start 70 %. A blind panel of three reviewers on preview frames
              ranked no fade first (8·sp second, 4·sp last). Every start position already keeps EDGE / GAP, so a
              solid mover at p 0 is never cut.
     D = the travel in % of the stage, per layer: TRAVEL (25), then
              limited so the product's outer edge on its entering side at the start (the landed spot moved away by the
              travel: the Dose's left edge, the Löffel's right edge) stays at least EDGE
              (16 px) inside the band and the screen, and, where an offer column stands right of the stage (desktop
              rows: .hp-set__col, .pdp-set__tx / __of), at least GAP (24 px) left of it, so the Löffel never slides
              under the heading or the price. That edge = the image's contain box at scale s minus the cut-out's
              transparent margin on the entering side (INSET, share of the box width, measured on the cut-outs of
              8 Oct: Dose 17 % left, Löffel 10 % right; a new cut-out means re-measuring, 0 for any other layer). With
              the bare contain box the Dose could travel only 6 px on a 390 px phone. Re-read at boot, listener start,
              image decode and resize.
     Start 70 % (founder 1 Nov 2026, „start it a bit later“; 80 % from 28 Oct, still on phones): the landing point is
     unchanged, so the run is 0.1·vh shorter and more of the Löffel's travel happens on screen.
     First screen: where a mover shows more than SEEN (40 px) of its product box on the page's first screen (homepage
     on a 1024 × 1366 iPad Pro in portrait or a 2560 × 1440 window, measured 1 Nov), the browser may already
     have painted the server's finished Set before this deferred script ran; moving the products apart would be a jump
     (with the old fade they vanished or stood half see-through instead). Until 3 Nov the Set stayed finished there, so
     on a tall desktop window the founder never saw it move („I just refreshed the homepage, and the animation, at least
     on the desktop, is still not live“, 3 Nov 2026). Since then the INTRO: snippets/set-intro, an inline script the
     section prints after the offer column, runs synchronously (a frame can show the finished Set first only after a
     network stall inside the offer column; on a cold load its lazy images are not there yet) and, from 1024 px
     with motion allowed, not printing, the composition on the current screen and no hash link to something below it,
     sets the movers to the start pose (dir · TRAVEL, TRAVEL = the composition's data-hp-travel, read here too), hides
     the labels (data-hp-wait), loads the images eagerly and marks data-hp-intro="set". Here, at boot: a mover shows more
     than SEEN px on the current screen → intro(): "run" (the inline fail-safe stands down; dirs() may limit a start
     where it would leave the band or touch the offer column, which never happens on a first-screen layout, measured
     3 Nov: a mover whose image has loaded, possibly painted at the inline pose, keeps TRAVEL, one not loaded yet takes
     the limited start, unseen), and once the Beutel's image and those of the movers on the screen have loaded and
     decoded (at most INTRO_WAIT; none of those movers loaded by then: the finished Set, no slide) plus INTRO_DELAY the
     scroll slide's paint() runs on time instead of scroll, t = 0 → 1 in INTRO_MS (same stagger 0.12·k, same
     smoothstep), the labels at the end, then finish(): no listener is left. Otherwise (no mover past SEEN on the
     screen, or below 1024 px by now) the server's landed styles come back in the same task and everything runs as
     before. Mark "back" (the inline fail-safe, which fires only at DOMContentLoaded or after 8 s when this script has
     not taken over, is sliding the movers home): left alone, it ends finished. Not marked (no inline script, the
     fail-safe already restored, or the composition off the screen at parse time) the Set stays finished and never
     assembles, also when the page boots further down (measured at scroll 0) or after a width change (rotation, window
     resize; not a toolbar that hides) into such a size (finish(), checked on resize and when the Set comes back into
     range). A boot further down with a mover already on the current screen before landing keeps the finished Set too,
     unless the inline script saw the composition on the screen: Chrome restores the scroll position of a reload or a
     back navigation (Shopify sends no-store, so no bfcache) before parsing, so there the intro replays, from the start
     pose in the first frame (no jump). Everywhere else at most SEEN px of a mover show on the first screen and the
     products assemble on scroll.
     Labels: .is-on at p ≥ 0.995 (all landed) once the images have decoded, off below 0.97. Reversible, no duration.
     The scroll listener lives only while the composition is within one screen. On desktop the movers follow the real
     progress from boot on (no wait for decoding: being solid, they must never stand at the start pose while the Set is
     already in view); the start limits use the images' width/height attributes until the natural sizes are known. */
  function setModule(root) {
    each(root.querySelectorAll('[data-hp-cmp]'), function (cmp) {
      if (cmp.classList.contains('hp-cmp--solo')) return;
      try { assemble(cmp); } catch (err) { fail(cmp); }
    });
  }
  // the cut-outs' transparent margin on the side each product enters from, in shares of the image's contain-box width
  // (measured on the 8 Oct cut-outs: Dose 17.4 % left, Löffel 10.0 % right; rounded down)
  var INSET = { tin: 0.17, spoon: 0.1 };
  // an image that has loaded (not broken); a promise for an image loaded (or failed) and decoded
  function loaded(img) { return !!(img && img.complete && img.naturalWidth); }
  function imgReady(img) {
    if (!img) return Promise.resolve();
    return new Promise(function (res) {
      if (img.complete) res();
      else { img.addEventListener('load', res); img.addEventListener('error', res); }
    }).then(function () { return img.decode ? img.decode().catch(function () {}) : null; });
  }
  // an image's aspect ratio: natural size once decoded, else the width/height attributes image_tag writes, else 1
  function aspect(img) {
    if (!img) return 1;
    var w = img.naturalWidth || num(img.getAttribute('width'), 0), h = img.naturalHeight || num(img.getAttribute('height'), 0);
    return w && h ? w / h : 1;
  }
  function assemble(cmp) {
    // snippets/set-intro's fail-safe is sliding the movers home (it ends with the finished Set): nothing to do here
    if (cmp.getAttribute('data-hp-intro') === 'back') return;
    // every layer, and the movers of the desktop slide (the Dose and the Löffel; the Beutel never moves there)
    var all = [], movers = [];
    each(cmp.querySelectorAll('.hp-ly'), function (el) {
      // the server's landed style (snippets/set-intro keeps it before it sets the intro's start pose)
      if (el.__hpTf0 == null) el.__hpTf0 = el.style.transform;
      var part = ['pouch', 'tin', 'spoon'].filter(function (k) { return el.classList.contains('hp-ly--' + k); })[0] || '';
      var l = { el: el, part: part, i: num(el.getAttribute('data-i'), 0), x: num(el.getAttribute('data-x'), 0),
        y: num(el.getAttribute('data-y'), 0), s: num(el.getAttribute('data-s'), 1), d0: num(el.getAttribute('data-dir'), 1),
        ins: INSET[part] || 0, k: 0, dir: 1, D: 0 };
      all.push(l);
      if (part !== 'pouch') { l.k = movers.length; movers.push(l); }
    });
    if (!all.length) return;
    // fly (below 1024 px): the live phone motion of 28 Oct, unchanged; slide (1024 px and wider): anim v3
    var fly = window.innerWidth < 1024;
    var lab = false, last = -1, ready = false, decoded = false, done = false, printing = false, ticking = false, listening = false, io = null;
    // start: the composition's top at 70 % of the screen height on desktop (founder 1 Nov), 80 % on phones (as live);
    // TRAVEL: % of the stage on desktop (the composition's data-hp-travel, also read by snippets/set-intro for the start
    // pose before first paint); EDGE / GAP: px a start box keeps from the band and screen edges / from the offer
    // column; SEEN: px of a mover's product box on the current screen above which a Set marked by set-intro plays the
    // intro, and on the page's first screen (or, at a boot further down before landing, on the current screen) above
    // which an unmarked desktop Set stays finished (no assembly)
    var START = 0.7, START_FLY = 0.8, TRAVEL = num(cmp.getAttribute('data-hp-travel'), 25), EDGE = 16, GAP = 24, SEEN = 40;
    // the desktop intro (3 Nov 2026): ms for t 0 → 1 (the Löffel lands at 0.88, the Dose at 1); max ms to wait for the
    // seen images; ms after they have decoded
    var INTRO_MS = 1200, INTRO_WAIT = 2500, INTRO_DELAY = 250;
    // the visible area's edges, looked up once: the header (it counts only while it covers the top of the screen) and
    // a bottom buy bar (#product-sticky-bar: the Beutel page's, or the Set section's own on the landing page)
    var head = document.querySelector('.launch-header, header.header--sticky');
    var bar = document.getElementById('product-sticky-bar'), barTop = null, sliding = false, follow = 0;
    // the bar's own script shows / hides it from its observers, possibly after the last scroll event: a class change
    // starts the slide, followed for its 0.2 s plus a margin (observed only while the scroll listener lives)
    var barMo = bar && window.MutationObserver ? new MutationObserver(function () { follow = performance.now() + 400; onScroll(); }) : null;
    // the band the composition sits in (the start boxes stay inside its left and right edge) and the offer column
    // beside the stage on desktop (the Löffel stops short of it)
    var band = cmp.closest('.hp-set, .pdp-set, .shopify-section'), stage = cmp.querySelector('.hp-cmp__stage');
    var cols = band ? band.querySelectorAll('.hp-set__col, .pdp-set__tx, .pdp-set__of') : [];
    function dirs() {
      if (fly) {
        // every layer from its block's enter_from, D from 32 % (390 px) to 60 % (558 px and wider) of the stage
        var D = Math.min(60, Math.max(32, 32 + (window.innerWidth - 390) / 6));
        all.forEach(function (l) { l.dir = l.d0; l.D = D; });
        return;
      }
      movers.forEach(function (l) { l.dir = l.part === 'tin' ? -1 : l.part === 'spoon' ? 1 : l.d0 < 0 ? -1 : 1; });
      if (!stage) { movers.forEach(function (l) { l.D = TRAVEL; }); return; }
      // layout reads only (boot, listener start, image decode, resize): the stage box is untransformed, the layers sit
      // inset 0 on it, so x % of a layer = x % of the stage width
      var st = stage.getBoundingClientRect(), S = st.width || 1, b = band ? band.getBoundingClientRect() : null;
      var vw = document.documentElement.clientWidth || window.innerWidth; // without a classic scrollbar
      var lo = Math.max(0, b ? b.left : 0) + EDGE, hi = Math.min(vw, b ? b.right : vw) - EDGE;
      // a column counts only where it stands right of the stage's centre (desktop rows)
      each(cols, function (c) {
        var r = c.getBoundingClientRect();
        if (r.width && r.left > st.left + S / 2) hi = Math.min(hi, r.left - GAP);
      });
      movers.forEach(function (l) {
        var a = aspect(l.el.querySelector('img'));
        var w = a >= 1 ? l.s * S : l.s * S * a, cx = st.left + S / 2 + l.x * S / 100, edge = w / 2 - l.ins * w;
        var room = l.dir < 0 ? cx - edge - lo : hi - cx - edge; // the landed leading edge's distance to its limit
        l.D = Math.max(0, Math.min(TRAVEL, 100 * room / S));
      });
    }
    dirs();
    // px of a mover's product box (the image's contain box inside the layer's scaled square; only its vertical extent
    // counts, which is the landed one at every p of the slide) on the page's first screen (atTop: as at scroll 0,
    // whatever the scroll position now) or on the current screen
    function boxPx(l, atTop) {
      var r = l.el.getBoundingClientRect(), a = aspect(l.el.querySelector('img'));
      var h = a >= 1 ? r.height / a : r.height, top = r.top + (r.height - h) / 2 + (atTop ? window.pageYOffset || 0 : 0);
      return Math.max(0, Math.min(viewH(), top + h) - Math.max(0, top));
    }
    function shows(atTop) { return movers.some(function (l) { return boxPx(l, atTop) > SEEN; }); }

    function progress() {
      // the frame's only layout reads, all taken before paint() writes: the box, the header's and the bar's edges
      var H = viewH(), r = cmp.getBoundingClientRect();
      var top = head ? Math.min(H, Math.max(0, head.getBoundingClientRect().bottom)) : 0, bot = H;
      if (bar) {
        var b = bar.getBoundingClientRect();
        if (b.height) { // not display: none
          sliding = barTop !== null && Math.abs(b.top - barTop) > 0.25;
          barTop = b.top;
          bot = Math.max(top, Math.min(H, b.top)); // hidden = moved below the screen (translateY(100%)): bot = H
        } else { sliding = false; barTop = null; } // display: none or detached mid-slide: stop following, start fresh
      }
      var from = (fly ? START_FLY : START) * H, to = (top + bot - r.height) / 2;
      return clamp((from - r.top) / Math.max(1, from - to));
    }
    function paint(p) {
      if (fly) {
        // every layer slides in from its side, grows from 0.7 and fades in; stagger by block order (data-i)
        all.forEach(function (l) {
          var sp = clamp(p / (1 - 0.12 * l.i)), se = sp * sp * (3 - 2 * sp), off = l.dir * l.D * (1 - se);
          l.el.style.transform = 'translate(' + (l.x + off).toFixed(2) + '%,' + l.y + '%) scale(' + (l.s * (0.7 + 0.3 * se)).toFixed(4) + ')';
          l.el.style.opacity = Math.min(1, 2.5 * sp).toFixed(3);
        });
      } else {
        // horizontal only, solid: y and scale are the landed values at every p; the Beutel keeps the server's style
        movers.forEach(function (l) {
          var sp = clamp(p / (1 - 0.12 * l.k)), se = sp * sp * (3 - 2 * sp);
          l.el.style.transform = 'translate(' + (l.x + l.dir * l.D * (1 - se)).toFixed(2) + '%,' + l.y + '%) scale(' + l.s + ')';
        });
      }
      if (!lab && p >= 0.995 && decoded) { lab = true; cmp.classList.add('is-on'); }
      else if (lab && p < 0.97) { lab = false; cmp.classList.remove('is-on'); }
    }
    function update() {
      ticking = false;
      if (!ready) return;
      var p = progress();
      // the bar is sliding in or out (0.2 s CSS transition): follow it frame by frame until it rests
      if (sliding || performance.now() < follow) onScroll();
      if (Math.abs(p - last) < 0.0005) return;
      last = p;
      paint(p);
    }
    function onScroll() { if (!ticking) { ticking = true; requestAnimationFrame(update); } }
    // every layer back to the server's landed style (no inline opacity)
    function reset() { all.forEach(function (l) { l.el.style.transform = l.el.__hpTf0; l.el.style.opacity = ''; }); }
    function willChange(on) {
      all.forEach(function (l) { l.el.style.willChange = on && (fly || l.part !== 'pouch') ? (fly ? 'transform, opacity' : 'transform') : ''; });
    }
    // a width change since the last check (rotation, window resize; an auto-hiding toolbar changes only the height and
    // must never finish the Set mid-scroll) into a desktop size where the Set is on the page's first screen
    var w0 = window.innerWidth;
    function intoFirstScreen() {
      var w = window.innerWidth;
      if (w === w0) return false;
      w0 = w;
      return !fly && shows(true);
    }
    // across 1024 px (a tablet turned, a window resized) the other motion takes over, from the landed styles
    function mode() {
      var f = window.innerWidth < 1024;
      if (f === fly) return;
      fly = f;
      reset();
      if (listening) willChange(true);
      if (printing) return;
      ready = !fly || decoded; // phones wait for the images, hidden at the start pose, as at boot
      if (!ready) paint(0);
    }
    function onResize() {
      mode();
      if (intoFirstScreen()) { finish(); return; }
      dirs(); last = -1; onScroll();
    }
    // for good: the finished Set (server styles, labels shown); no scroll/resize listener or observer is left (the
    // print listeners return on done)
    function finish() {
      done = true;
      ready = false;
      if (io) io.disconnect(); // none on the intro path
      cmp.removeAttribute('data-hp-intro');
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onResize);
      if (barMo) barMo.disconnect();
      listening = false;
      reset();
      willChange(false);
      disarm(cmp);
    }
    function listen(on) {
      if (done || on === listening) return;
      if (on) mode(); // the width may have crossed 1024 px while nothing listened
      if (on && intoFirstScreen()) { finish(); return; } // the width changed while the Set was out of range
      listening = on;
      willChange(on);
      if (on) {
        dirs();
        last = -1;
        window.addEventListener('scroll', onScroll, { passive: true });
        window.addEventListener('resize', onResize, { passive: true });
        if (barMo) barMo.observe(bar, { attributes: true, attributeFilter: ['class'] });
        onScroll();
      } else {
        window.removeEventListener('scroll', onScroll);
        window.removeEventListener('resize', onResize);
        if (barMo) barMo.disconnect();
        last = -1;
        update();
      }
    }

    // the desktop intro: once, on time, from the start pose that snippets/set-intro set before first paint
    function intro() {
      cmp.setAttribute('data-hp-intro', 'run'); // taken over: the inline fail-safe stands down
      // a mover whose image has loaded may already be painted at the inline pose (dir · TRAVEL): it keeps it (no jump);
      // one not loaded yet takes dirs()'s start, unseen (the same today: no limit binds on a first-screen layout)
      movers.forEach(function (l) { if (loaded(l.el.querySelector('img'))) l.D = TRAVEL; });
      arm(cmp);
      willChange(true);
      paint(0);
      var t0 = 0;
      function end() { window.removeEventListener('beforeprint', end); finish(); }
      // an error from here on runs outside setModule's try: the finished Set, as fail() leaves any module
      function stop() { done = true; window.removeEventListener('beforeprint', end); cmp.removeAttribute('data-hp-intro'); fail(cmp); }
      function step(now) {
        if (done) return;
        try {
          if (!t0) t0 = now;
          var t = clamp((now - t0) / INTRO_MS);
          paint(t); // the labels come on at t ≥ 0.995
          if (t < 1) requestAnimationFrame(step);
          else setTimeout(end, 700); // the labels' own transition (≈ 560 ms), then the plain finished Set
        } catch (err) { stop(); }
      }
      window.addEventListener('beforeprint', end); // print: the finished Set at once
      // the images that are seen: the Beutel and every mover whose box shows (a Löffel below the fold must not hold the
      // Dose back), loaded and decoded, at most INTRO_WAIT ms
      var seen = all.filter(function (l) { return l.part === 'pouch' || boxPx(l, false) > 0; });
      Promise.race([
        Promise.all(seen.map(function (l) { return imgReady(l.el.querySelector('img')); })),
        new Promise(function (res) { setTimeout(res, INTRO_WAIT); })
      ]).then(function () {
        if (done || !cmp.isConnected) return;
        try {
          // no seen mover has loaded: no slide (it would run unseen, then the labels would point at empty space); the
          // finished Set, and the products appear landed when they load
          if (!seen.some(function (l) { return l.part !== 'pouch' && loaded(l.el.querySelector('img')); })) { end(); return; }
          decoded = true;
          setTimeout(function () { requestAnimationFrame(step); }, INTRO_DELAY);
        } catch (err) { stop(); }
      });
    }
    if (cmp.getAttribute('data-hp-intro') === 'set') {
      if (!fly && shows(false)) { intro(); return; }
      // the inline script's start pose, but no mover past SEEN on the screen now (or below 1024 px by now): the
      // server's landed layers again, in this task (nothing is painted in between), and everything as before; the
      // composition stays armed (a disarm and re-arm would fade the labels out), the two finished cases disarm it
      reset();
      cmp.removeAttribute('data-hp-intro');
    }
    if (!fly && shows(true)) {
      /* desktop: a mover shows on the page's first screen (the server's finished Set may have been painted before this
         deferred script ran): no assembly at all, the finished Set stays, also after a boot further down the page;
         moving it apart would be a visible jump, and at the top of the page it would stand there unassembled */
      disarm(cmp);
      return;
    }
    var p0 = progress();
    if (p0 >= 0.995) {
      /* already landed on screen (or scrolled past): keep the end state, labels on in the same frame (no fade) */
      cmp.classList.add('is-on');
      lab = true;
      decoded = true;
      arm(cmp);
      ready = true;
      last = p0;
    } else if (!fly && shows(false)) {
      /* desktop, a boot further down the page (reload, back navigation without bfcache, a hash link) with a mover
         already on the current screen before landing: the browser may have painted the finished Set there, so it stays */
      disarm(cmp);
      return;
    } else {
      arm(cmp);
      if (fly) {
        paint(0); // phones (as live): hidden at the start pose until the images have decoded
      } else {
        ready = true;
        paint(p0); // desktop: the real progress at once; the movers are solid, so they never wait at the start pose
        last = p0;
      }
      var imgs = cmp.querySelectorAll('img');
      Promise.all(Array.prototype.map.call(imgs, function (img) {
        return img.decode ? img.decode().catch(function () {}) : Promise.resolve();
      })).then(function () {
        if (!cmp.isConnected || done) return;
        decoded = true; // the labels may show now (they point at the products)
        if (!printing) ready = true;
        dirs(); // natural sizes known (the width/height attributes gave the same aspect before)
        last = -1;
        update();
      });
    }
    io = new IntersectionObserver(function (es) {
      es.forEach(function (e) { listen(e.isIntersecting); });
    }, { rootMargin: '100% 0px 100% 0px' });
    io.observe(cmp);
    // print and „save as PDF“: the finished Set (the module-level beforeprint handler disarms, so the labels show; the
    // layers go to their landed styles here, and nothing repaints them while the print dialog is open, e.g. when an
    // image decodes), then back to the scroll pose and the armed labels
    window.addEventListener('beforeprint', function () {
      if (done) return;
      printing = true;
      ready = false;
      reset();
    });
    window.addEventListener('afterprint', function () {
      if (!printing || done || !cmp.isConnected) return;
      printing = false;
      arm(cmp);
      ready = !fly || decoded; // phones keep waiting for the images, hidden at the start pose, as at boot
      if (!ready) paint(0);
      lab = false;
      cmp.classList.remove('is-on');
      last = -1;
      update();
    });
  }

  /* ---------- Pasta Pomodoro ----------
     phone: the main image scales in (.95 → 1, 800 ms; opacity 700 ms) at IO 0.15.
     desktop: the fan is scroll-linked (fan() below): the cards slide out from under the main image while the gallery
     scrolls in and slide back under when it scrolls out again.
     stat (IO 0.15 on the proof block; on desktop not before the fan has opened, p ≥ .98): t 0 before-figure from −16 px ·
     t 200 arrow from −10 px · t 400 after-figure scale(.82) → 1 and the count from → to (count_ms, one decimal) with
     the „Mit" bar r → 1 in sync · t 400 the „Ohne" bar scaleX(0) → none in 400 ms. */
  function pasta(root) {
    var dur = num(root.getAttribute('data-hp-count-ms'), 4000);
    var main = root.querySelector('[data-hp-pasta-main]');
    if (main && shown(main) && !passed(main)) {
      arm(main);
      once(main, 0.15, function () {
        disarm(main);
        rise(main.querySelector('img') || main, 'scale(.95)', 0, 800, 700, EASE_IO);
      });
    }

    var fanDone = true, inView = false, fired = false, fanSync = null;
    var gal = root.querySelector('[data-hp-pasta-fan]');
    if (gal && shown(gal)) {
      var m = gal.querySelector('[data-fan="main"]'), cards = gal.querySelectorAll('[data-fan="card"]');
      if (m && cards.length) {
        fanDone = false;
        fanSync = fan(gal, m, cards, function () { fanDone = true; fire(); });
      }
    }

    var proof = root.querySelector('[data-hp-pasta-proof]');
    if (!proof || passed(proof)) return;
    var ctEl = proof.querySelector('[data-hp-count]');
    var ct = ctEl ? countEl(ctEl) : null;
    var barA = proof.querySelector('[data-bar="after"]'), barB = proof.querySelector('[data-bar="before"]');
    var r = barA ? clamp(num(barA.getAttribute('data-r'), 0.552)) : 0.552;
    arm(proof);
    if (ct) ct.el.textContent = fmt(ct.from, ct.dec);
    if (barA) barA.style.transform = 'scaleX(' + r + ')';

    function fire() {
      // the fan may never have been painted at p ≥ .98 (hidden below 1024 px after boot, or out of its listening range):
      // paint its real state now — a hidden or passed gallery reads p = 1 and opens the stat
      if (!fanDone && inView && fanSync) fanSync();
      if (fired || !fanDone || !inView) return;
      fired = true;
      disarm(proof);
      rise(proof.querySelector('[data-ps="before"]'), 'translateX(-16px)', 0, 520, 520, EASE_OUT, 'ease');
      rise(proof.querySelector('[data-ps="arrow"]'), 'translateX(-10px)', 200, 520, 520, EASE_OUT, 'ease');
      rise(proof.querySelector('[data-ps="after"]'), 'scale(.82)', 400, 650, 500, EASE_OUT, 'ease');
      anim(barB, [{ transform: 'scaleX(0)' }, { transform: 'none' }], { duration: 400, delay: 400, easing: EASE_OUT, fill: 'backwards' });
      setTimeout(function () {
        if (!ct) { if (barA) barA.style.transform = ''; return; }
        countUp(ct.el, ct.from, ct.to, dur, ct.dec, function (e) {
          if (barA) barA.style.transform = e >= 1 ? '' : 'scaleX(' + (r + (1 - r) * e).toFixed(4) + ')';
        });
      }, 400);
    }
    once(proof, 0.15, function () { inView = true; fire(); });
  }

  /* ---------- Pasta fan: scroll-linked (founder 9 Oct 2026) ----------
     The launch page's fan, back on the homepage: the founder found the one-shot fly-in with a fade (7 Oct) weaker.
     Desktop only (the fan is display:none below 1024 px). The cards sit behind the main image (z-index in
     section-hp.css) at its centre, scale .8, and slide out to their places as the gallery scrolls in; scrolling back
     up slides them under again. p = 0 when the gallery's top reaches the bottom of the screen, p = 1 when it is
     centred, linear in between (as on the launch page). Each card stays hidden while it is still entirely under the
     main image and turns opaque in the last step before its first edge comes out (measured per card from the layout),
     so the switch is never seen: the cards slide out, they do not fade. The main image: scale .95 → 1 and opacity
     0 → 1, opaque by the time the first card turns visible. onDone runs once at p ≥ .98 (the stat waits for it).
     Returns a sync function (paint the real state now), which the stat calls when it comes into view. Print shows the
     finished fan through an @media print rule in section-hp.css (the start state is inline here, not data-hp-wait).
     Scroll and resize are only listened to while the gallery is within a quarter screen of the viewport. */
  function fan(gal, m, cards, onDone) {
    var offs = [], last = -1, ticking = false, listening = false, done = false, pMain = 0.25;
    function measure() {
      if (!m.offsetWidth) return; // the fan is display:none (below 1024 px): keep the last good layout
      var mw = m.offsetWidth, mh = m.offsetHeight, mx = m.offsetLeft + mw / 2, my = m.offsetTop + mh / 2;
      offs = Array.prototype.map.call(cards, function (c) {
        var w = c.offsetWidth, h = c.offsetHeight, cx = c.offsetLeft + w / 2, cy = c.offsetTop + h / 2;
        var o = { el: c, dx: mx - cx, dy: my - cy, show: 0 };
        // the first p at which this card is no longer entirely under the main image; visible one step (.01) earlier
        // (the main image is scaled .95 → 1 about its centre at the same time, so its visible box is smaller early on)
        for (var i = 0; i <= 200; i++) {
          var p = i / 200, k = 1 - p, s = 0.8 + 0.2 * p, x = cx + o.dx * k, y = cy + o.dy * k;
          var hw = (0.95 + 0.05 * p) * mw / 2, hh = (0.95 + 0.05 * p) * mh / 2;
          if (x - s * w / 2 < mx - hw || x + s * w / 2 > mx + hw || y - s * h / 2 < my - hh || y + s * h / 2 > my + hh) {
            o.show = Math.max(0, p - 0.01);
            break;
          }
        }
        return o;
      });
      pMain = Math.max(0.05, Math.min.apply(null, offs.map(function (o) { return o.show; }).concat([0.25])));
    }
    function progress() {
      var H = viewH(), r = gal.getBoundingClientRect();
      return clamp((H - r.top) / (H / 2 + r.height / 2));
    }
    function paint(p) {
      var k = 1 - p;
      m.style.transform = 'scale(' + (0.95 + 0.05 * p).toFixed(4) + ')';
      m.style.opacity = Math.min(1, p / pMain).toFixed(3);
      offs.forEach(function (o) {
        o.el.style.transform = 'translate(' + (o.dx * k).toFixed(1) + 'px,' + (o.dy * k).toFixed(1) + 'px) scale(' + (0.8 + 0.2 * p).toFixed(4) + ')';
        o.el.style.opacity = p >= o.show ? '1' : '0';
      });
      if (!done && p >= 0.98) { done = true; onDone(); }
    }
    function update() {
      ticking = false;
      var p = progress();
      if (Math.abs(p - last) < 0.0005) return;
      last = p;
      paint(p);
    }
    function onScroll() { if (!ticking) { ticking = true; requestAnimationFrame(update); } }
    function onResize() { measure(); last = -1; onScroll(); }
    function listen(on) {
      if (on === listening) return;
      listening = on;
      each(cards, function (c) { c.style.willChange = on ? 'transform' : ''; });
      if (on) {
        window.addEventListener('scroll', onScroll, { passive: true });
        window.addEventListener('resize', onResize, { passive: true });
        measure(); // the layout may have changed while nothing listened (a resize across 1024 px)
        last = -1;
        onScroll();
      } else {
        window.removeEventListener('scroll', onScroll);
        window.removeEventListener('resize', onResize);
        last = -1;
        update(); // a fast scroll out of range still lands on the right end state
      }
    }
    measure();
    paint(progress()); // the start (or end) state before the first frame
    new IntersectionObserver(function (es) { listen(es[es.length - 1].isIntersecting); }, { rootMargin: '25% 0px 25% 0px' }).observe(gal);
    return function () { last = -1; update(); };
  }

  /* ---------- Ein Löffel: one scene ----------
     IO 0.15, once: t 0 the main image (translateY 30 px + scale .97; opacity 700 ms, transform 800 ms) ·
     t 350 + j·120 the secondaries (20 px + scale .95; 600 / 700 ms), phones too · the text at t 350 phone /
     200 desktop (20 px; 600 / 700 ms). No parallax. */
  function scene(root) {
    if (passed(root)) return;
    arm(root);
    once(root, 0.15, function () {
      disarm(root);
      rise(root.querySelector('[data-hp-lo="main"]'), 'translateY(30px) scale(.97)', 0, 800, 700, EASE_IO);
      each(root.querySelectorAll('[data-hp-lo="sec"]'), function (x) {
        if (!shown(x)) return;
        var j = num(x.style.getPropertyValue('--j'), 0);
        rise(x, 'translateY(20px) scale(.95)', 350 + j * 120, 700, 600, EASE_IO);
      });
      rise(root.querySelector('[data-hp-lo="text"]'), 'translateY(20px)', window.innerWidth >= 1200 ? 200 : 350, 700, 600, EASE_IO);
    });
  }

  /* ---------- Dein Proteinbedarf ----------
     IO 0.15 once: t 400 the figure (opacity 500 ms ease, scale .82 → 1 in 650 ms ease-out) and the count
     from → to (count_ms, one decimal). Desktop with data-hp-widen: the grey ::before scaleX(.6) → none in 1,000 ms
     (CSS, .is-go). Body and source never move. */
  function bedarf(root) {
    if (passed(root)) return;
    var dur = num(root.getAttribute('data-hp-count-ms'), 4000);
    var stat = root.querySelector('[data-hp-stat]');
    var ctEl = root.querySelector('[data-hp-count]');
    var ct = ctEl ? countEl(ctEl) : null;
    arm(root);
    if (ct) ct.el.textContent = fmt(ct.from, ct.dec);
    once(root, 0.15, function () {
      root.classList.add('is-go');
      disarm(root);
      if (stat) rise(stat, 'scale(.82)', 400, 650, 500, EASE_OUT, 'ease');
      if (ct) setTimeout(function () { countUp(ct.el, ct.from, ct.to, dur, ct.dec); }, 400);
    });
  }

  /* ---------- PDP „Was ändert sich?" dish cards: one count group ----------
     Every [data-cg-item] counts its „mit" value from „ohne" in 1,200 ms together, the added bars scaleX(r → 1) in
     sync. Trigger: the first figure fully visible between the header and the sticky bar. A group already passed keeps
     its final values. „+9,9 g" and the basis line never move. */
  function dishes(root) {
    var items = [];
    each(root.querySelectorAll('[data-cg-item]'), function (it) {
      var c = it.querySelector('[data-hp-count]');
      if (!c) return;
      var bar = it.querySelector('[data-addbar]');
      var x = countEl(c);
      x.bar = bar;
      x.r = bar ? clamp(num(bar.getAttribute('data-r'), 0)) : 0;
      items.push(x);
    });
    if (!items.length || passed(root)) return;
    arm(root);
    items.forEach(function (x) {
      x.el.textContent = String(x.from).replace('.', ',');
      if (x.bar) x.bar.style.transform = 'scaleX(' + x.r + ')';
    });
    var sticky = document.getElementById('product-sticky-bar');
    var bot = sticky && sticky.offsetHeight ? sticky.offsetHeight : 80;
    var io = new IntersectionObserver(function (es) {
      es.forEach(function (e) {
        if (!e.isIntersecting || e.intersectionRatio < 0.99) return;
        io.disconnect();
        disarm(root);
        items.forEach(function (x) {
          countUp(x.el, x.from, x.to, 1200, x.dec, function (k) {
            if (x.bar) x.bar.style.transform = k >= 1 ? '' : 'scaleX(' + (x.r + (1 - x.r) * k).toFixed(4) + ')';
          });
        });
      });
    }, { rootMargin: '-' + headerH() + 'px 0px -' + bot + 'px 0px', threshold: [0.99, 1] });
    io.observe(items[0].el);
  }

  /* ---------- boot ---------- */
  var MODULES = { set: setModule, pasta: pasta, scene: scene, bedarf: bedarf, dishes: dishes };
  function initRoot(root) {
    if (root.__hpInit) return;
    var fn = MODULES[root.getAttribute('data-hp-motion')];
    if (!fn) return;
    root.__hpInit = true;
    try { fn(root); } catch (err) { fail(root); }
  }
  function initScope(scope) {
    each(scope.querySelectorAll('[data-hp-motion]'), initRoot);
    if (scope.hasAttribute && scope.hasAttribute('data-hp-motion')) initRoot(scope);
    try { reveals(scope); } catch (err) {
      each(scope.querySelectorAll('[data-hp-reveal], [data-hp-reveal-group]'), disarm);
    }
  }
  function boot() { initScope(document); }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
  /* sections re-rendered outside the editor (Section Rendering API) get their own init */
  document.addEventListener('shopify:section:load', function (e) { if (e.target) initScope(e.target); });
  /* print and "save as PDF" show the finished page */
  window.addEventListener('beforeprint', function () { each(document.querySelectorAll('[' + WAIT + ']'), disarm); });
})();
